import { CoreEvent, eventNames, useCoreEvents } from '@carvana/core-events';
import { useCallback, useEffect, useState } from 'react';
import { SOFTPULL_MODULE } from '~/constants/common/MfeModules';
import { selectIsMfeReady, useMfeLoadStore } from '~/state/common/MfeLoadState';

const selectIsSoftPullMfeReady = selectIsMfeReady(SOFTPULL_MODULE);

type SoftpullModalOpenPayload = {
  isAddCoSigner: boolean;
  isEditCoSigner: boolean;
  landingHeading?: string;
  landingSubheading?: string;
  showLogo?: boolean;
};

type OwnProps = {
  canMount: boolean;
  isAddCoApplicantModalOpen: boolean;
  isEditCoApplicantModalOpen: boolean;
  isSoftPullModalOpen: boolean;
  landingHeading?: string;
  landingSubheading?: string;
  showLogo?: boolean;
  setAddCoApplicantModalOpen: (isOpen: boolean) => void;
  setEditCoApplicantModalOpen: (isOpen: boolean) => void;
  setSoftPullModalOpen: (isOpen: boolean) => void;
};

export default function useToggleModal({
  canMount,
  isAddCoApplicantModalOpen,
  isEditCoApplicantModalOpen,
  isSoftPullModalOpen,
  landingHeading,
  landingSubheading,
  showLogo,
  setAddCoApplicantModalOpen,
  setEditCoApplicantModalOpen,
  setSoftPullModalOpen
}: OwnProps) {
  const isReadyFromStore = useMfeLoadStore(selectIsSoftPullMfeReady);

  // Fallback for pages that don't mount MfeEventsListener (Home, FLP, Drive).
  const [isReadyFromListener, setReadyFromListener] = useState(false);
  useCoreEvents(
    eventNames.softpullModal.ready,
    useCallback(() => setReadyFromListener(true), [])
  );

  // Also gated on canMount — see useMfeMountGate — to avoid reading a stale
  // pre-teardown value during a same-commit SPA swap.
  const isReady = canMount && (isReadyFromStore || isReadyFromListener);

  useEffect(() => {
    if (isReady) return () => {};

    // Failsafe to close the modal if the MFE doesn't signal it is ready within 10 seconds
    const t = setTimeout(() => {
      setAddCoApplicantModalOpen(false);
      setEditCoApplicantModalOpen(false);
      setSoftPullModalOpen(false);
    }, 10000);

    return () => clearTimeout(t);
  }, [
    isReady,
    setAddCoApplicantModalOpen,
    setEditCoApplicantModalOpen,
    setSoftPullModalOpen
  ]);

  useEffect(() => {
    if (!isReady) return;

    if (
      isSoftPullModalOpen ||
      isAddCoApplicantModalOpen ||
      isEditCoApplicantModalOpen
    ) {
      const payload: SoftpullModalOpenPayload = {
        isAddCoSigner: isAddCoApplicantModalOpen,
        isEditCoSigner: isEditCoApplicantModalOpen,
        landingHeading,
        landingSubheading,
        showLogo
      };
      CoreEvent.emit(eventNames.softpullModal.open, payload as any);

      const closeHandler = () => {
        setSoftPullModalOpen(false);
        setAddCoApplicantModalOpen(false);
        setEditCoApplicantModalOpen(false);
      };
      CoreEvent.once(eventNames.softpullModal.close, closeHandler);

      return () => CoreEvent.off(eventNames.softpullModal.close, closeHandler);
    } else {
      CoreEvent.emit(eventNames.softpullModal.close, {});
    }
  }, [
    isAddCoApplicantModalOpen,
    isEditCoApplicantModalOpen,
    isReady,
    isSoftPullModalOpen,
    landingHeading,
    landingSubheading,
    showLogo,
    setAddCoApplicantModalOpen,
    setEditCoApplicantModalOpen,
    setSoftPullModalOpen
  ]);
}
